import "@dxos/node-std/globals"

// packages/common/feed-store/src/feed-factory.ts
import hypercore from "hypercore";
import { sha256 } from "@dxos/crypto";
import { failUndefined } from "@dxos/debug";
import { createCrypto } from "@dxos/hypercore";
import { log } from "@dxos/log";
var FeedFactory = class {
  // TODO(burdon): Must patch codec here createCodecEncoding.
  constructor({ root, signer, hypercore: hypercore2 }) {
    this._root = root != null ? root : failUndefined();
    this._signer = signer;
    this._hypercoreOptions = hypercore2;
    this._storage = (publicKey) => (filename) => {
      const dir = this._root.createDirectory(publicKey.toHex());
      const { type, native } = dir.getOrCreateFile(filename);
      log("created", {
        path: `${type}:${this._root.path}/${publicKey.truncate()}/${filename}`
      }, {
        file: "feed-factory.ts",
        line: 53,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      return native;
    };
  }
  createFeed(publicKey, options) {
    if ((options == null ? void 0 : options.writable) && !this._signer) {
      throw new Error("Signer required to create writable feeds.");
    }
    if (options == null ? void 0 : options.secretKey) {
      console.warn("Secret key ignored due to signer.");
    }
    const key = sha256(publicKey.toHex());
    const opts = Object.assign({}, this._hypercoreOptions, {
      secretKey: this._signer && (options == null ? void 0 : options.writable) ? Buffer.from("secret") : void 0,
      crypto: this._signer ? createCrypto(this._signer, publicKey) : void 0,
      onwrite: options == null ? void 0 : options.onwrite
    }, options);
    return hypercore(this._storage(publicKey), key, opts);
  }
};

// packages/common/feed-store/src/feed-wrapper.ts
import assert from "@dxos/node-std/assert";
import { inspect } from "@dxos/node-std/util";
import { Transform } from "streamx";
import { Trigger } from "@dxos/async";
import { inspectObject, StackTrace } from "@dxos/debug";
import { log as log2 } from "@dxos/log";
import { createBinder } from "@dxos/util";
var FeedWrapper = class {
  constructor(_hypercore, _key) {
    this._hypercore = _hypercore;
    this._key = _key;
    this._pendingWrites = /* @__PURE__ */ new Set();
    this._binder = createBinder(this._hypercore);
    this._writeLock = new Trigger();
    this._closed = false;
    this.on = this._binder.fn(this._hypercore.on);
    this.off = this._binder.fn(this._hypercore.off);
    this.open = this._binder.async(this._hypercore.open);
    this._close = this._binder.async(this._hypercore.close);
    this.close = async () => {
      if (this._pendingWrites.size) {
        log2.warn("Closing feed with pending writes", {
          feed: this._key,
          count: this._pendingWrites.size,
          pendingWrites: Array.from(this._pendingWrites.values()).map((stack) => stack.getStack())
        }, {
          file: "feed-wrapper.ts",
          line: 140,
          scope: this,
          callSite: (f, a) => f(...a)
        });
      }
      this._closed = true;
      await this._close();
    };
    this.get = this._binder.async(this._hypercore.get);
    this.append = this._binder.async(this._hypercore.append);
    this.download = this._binder.async(this._hypercore.download);
    this.replicate = this._binder.fn(this._hypercore.replicate);
    assert(this._hypercore);
    assert(this._key);
    this._writeLock.wake();
  }
  [inspect.custom]() {
    return inspectObject(this);
  }
  toJSON() {
    return {
      feedKey: this._key,
      length: this.properties.length,
      opened: this.properties.opened,
      closed: this.properties.closed
    };
  }
  get key() {
    return this._key;
  }
  get core() {
    return this._hypercore;
  }
  // TODO(burdon): Create proxy.
  get properties() {
    return this._hypercore;
  }
  createReadableStream(opts) {
    const self = this;
    const transform = new Transform({
      transform(data, cb) {
        void self._writeLock.wait().then(() => {
          this.push(data);
          cb();
        });
      }
    });
    this._hypercore.createReadStream(opts).pipe(transform, (err) => {
    });
    return transform;
  }
  createFeedWriter() {
    return {
      write: async (data, { afterWrite } = {}) => {
        log2("write", {
          feed: this._key,
          seq: this._hypercore.length,
          data
        }, {
          file: "feed-wrapper.ts",
          line: 88,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        assert(!this._closed, "Feed closed");
        const stackTrace = new StackTrace();
        try {
          this._pendingWrites.add(stackTrace);
          if (this._pendingWrites.size === 1) {
            this._writeLock.reset();
          }
          const seq = await this.append(data);
          assert(seq < this.length, "Invalid seq after write");
          log2("write complete", {
            feed: this._key,
            seq
          }, {
            file: "feed-wrapper.ts",
            line: 101,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          const receipt = {
            feedKey: this.key,
            seq
          };
          await (afterWrite == null ? void 0 : afterWrite(receipt));
          return receipt;
        } finally {
          this._pendingWrites.delete(stackTrace);
          if (this._pendingWrites.size === 0) {
            this._writeLock.wake();
          }
        }
      }
    };
  }
  get opened() {
    return this._hypercore.opened;
  }
  get closed() {
    return this._hypercore.closed;
  }
  get length() {
    return this._hypercore.length;
  }
};

// packages/common/feed-store/src/feed-store.ts
import assert2 from "@dxos/node-std/assert";
import { Event, sleep } from "@dxos/async";
import { failUndefined as failUndefined2 } from "@dxos/debug";
import { PublicKey } from "@dxos/keys";
import { log as log3 } from "@dxos/log";
import { ComplexMap } from "@dxos/util";
var FeedStore = class {
  constructor({ factory }) {
    this._feeds = new ComplexMap(PublicKey.hash);
    this._closed = false;
    this.feedOpened = new Event();
    this._factory = factory != null ? factory : failUndefined2();
  }
  get size() {
    return this._feeds.size;
  }
  get feeds() {
    return Array.from(this._feeds.values());
  }
  /**
  * Get the open feed if it exists.
  */
  getFeed(publicKey) {
    return this._feeds.get(publicKey);
  }
  /**
  * Gets or opens a feed.
  * The feed is readonly unless a secret key is provided.
  */
  async openFeed(feedKey, { writable } = {}) {
    log3("opening feed", {
      feedKey
    }, {
      file: "feed-store.ts",
      line: 55,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    assert2(feedKey);
    assert2(!this._closed, "Feed store is closed");
    let feed = this.getFeed(feedKey);
    if (feed) {
      if (writable && !feed.properties.writable) {
        throw new Error(`Read-only feed is already open: ${feedKey.truncate()}`);
      } else {
        await feed.open();
        return feed;
      }
    }
    const core = this._factory.createFeed(feedKey, {
      writable
    });
    feed = new FeedWrapper(core, feedKey);
    this._feeds.set(feed.key, feed);
    await feed.open();
    this.feedOpened.emit(feed);
    log3("opened", {}, {
      file: "feed-store.ts",
      line: 78,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    return feed;
  }
  /**
  * Close all feeds.
  */
  async close() {
    log3("closing...", {}, {
      file: "feed-store.ts",
      line: 86,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    this._closed = true;
    await Promise.all(Array.from(this._feeds.values()).map(async (feed) => {
      await feed.close();
      assert2(feed.closed);
      await sleep(100);
    }));
    this._feeds.clear();
    log3("closed", {}, {
      file: "feed-store.ts",
      line: 100,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
};

export {
  FeedFactory,
  FeedWrapper,
  FeedStore
};
//# sourceMappingURL=chunk-6Z3M43BG.mjs.map
